import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-24">
      <div className="container mx-auto px-6 max-w-4xl">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last Updated: December 2024</p>

          <div className="space-y-8 text-slate-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using the Texas House Cleaning website and services, you accept and agree to be bound by these 
                Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our website or services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Services Description</h2>
              <p className="mb-4">
                Texas House Cleaning provides residential cleaning services throughout Texas, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Standard Cleaning Services</li>
                <li>Deep Cleaning Services</li>
                <li>Move-Out Cleaning Services</li>
                <li>Custom Cleaning Plans</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Booking and Scheduling</h2>
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">3.1 Quote Requests</h3>
              <p>
                Quote requests submitted through our website are not binding. We will contact you to discuss your needs and 
                provide a formal quote.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">3.2 Service Confirmation</h3>
              <p>
                Services are confirmed only upon receipt of your acceptance of our quote and any required deposit or payment.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">3.3 Cancellations</h3>
              <p>
                Cancellations must be made at least 24 hours before the scheduled service time. Late cancellations may be 
                subject to a cancellation fee.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Pricing and Payment</h2>
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">4.1 Quotes</h3>
              <p>
                All quotes are estimates based on the information provided. Final pricing may vary based on actual conditions 
                and scope of work.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">4.2 Payment Terms</h3>
              <p>
                Payment is due upon completion of services unless otherwise agreed in writing. We accept various payment methods 
                as communicated during booking.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Client Responsibilities</h2>
              <p className="mb-4">As a client, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate information about your property and cleaning needs</li>
                <li>Ensure safe access to the property for our cleaning team</li>
                <li>Secure or remove valuables and fragile items</li>
                <li>Inform us of any pets, hazards, or special conditions</li>
                <li>Provide necessary cleaning supplies if specified in the service agreement</li>
                <li>Be available for questions or clarifications as needed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Our Responsibilities</h2>
              <p className="mb-4">We commit to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide professional and reliable cleaning services</li>
                <li>Use safe and eco-friendly cleaning products (unless otherwise requested)</li>
                <li>Respect your property and privacy</li>
                <li>Maintain appropriate insurance coverage</li>
                <li>Respond to concerns and complaints promptly</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Satisfaction Guarantee</h2>
              <p>
                We strive for 100% customer satisfaction. If you are not satisfied with our service, please contact us within 
                24 hours of service completion. We will work with you to address any concerns and may offer to re-clean specific 
                areas at no additional charge.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Liability and Insurance</h2>
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">8.1 Insurance</h3>
              <p>
                Texas House Cleaning maintains appropriate liability insurance to cover damages that may occur during our services.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">8.2 Limitations</h3>
              <p>
                We are not liable for damage to items that are inherently fragile, improperly maintained, or not disclosed to us 
                prior to service. We are not responsible for pre-existing damage or wear.
              </p>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-2 mt-4">8.3 Claims</h3>
              <p>
                Any claims for damage must be reported within 24 hours of service completion.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, and images, is the property of Texas House Cleaning 
                and is protected by copyright laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Website Use</h2>
              <p className="mb-4">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the website for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Transmit viruses or malicious code</li>
                <li>Interfere with the proper functioning of the website</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Termination</h2>
              <p>
                We reserve the right to refuse service or terminate our relationship with any client who violates these Terms 
                or engages in abusive or inappropriate behavior toward our staff.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated 
                "Last Updated" date. Your continued use of our services constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Governing Law</h2>
              <p>
                These Terms are governed by the laws of the State of Texas. Any disputes arising from these Terms or our services 
                shall be resolved in the courts of Texas.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact Information</h2>
              <p className="mb-4">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-slate-50 p-6 rounded-lg">
                <p className="font-semibold text-slate-900">Texas House Cleaning</p>
                <p>Email: clean.texashouse@gmail.com</p>
                <p>Phone: (346) 203-1728</p>
                <p>Service Area: Throughout Texas</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Acknowledgment</h2>
              <p>
                By using our website and services, you acknowledge that you have read, understood, and agree to be bound by 
                these Terms of Service.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
